import { useContext } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { rem } from "@mantine/core";
import { useForm, UseFormReturnType, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import { NewbornPostpartumPhysicalExamTypes } from "@/fhir/enums/newborn-postpartum-physical-exams";
import { NewbornPostpartumPhysicalExamsQuestionnaires } from "@/fhir/enums/questionnaires";
import usePhysicalExamInitialValues from "@/physical-exams/hooks/use-physical-exam-initial-values";
import usePhysicalExamQuestionnaire from "@/physical-exams/hooks/use-physical-exam-questionnaire";
import usePhysicalExamUnsavedChanges from "@/physical-exams/hooks/usePhysicalExamUnsavedChanges";
import PhysicalExamHeader from "@/physical-exams/PhysicalExamHeader";
import PhysicalExamList from "@/physical-exams/PhysicalExamList";
import {
  amendPhysicalExamQuestionnaireResponse,
  createPhysicalExamQuestionnaireResponse,
  getMainQuestionnaireUrlFromEncounter,
} from "@/physical-exams/services/physical-exams";
import { CHEST_AND_ABDOMEN_FORM_INITIAL_VALUES } from "@/physical-exams/types/newborn-postpartum-chest-and-abdomen";
import { PhysicalExamProps } from "@/physical-exams/types/physical-exams";
import { PhysicalExamForm } from "@/physical-exams/zod-schemas/physical-exams";
import { useTemporarySubmit } from "@/utils/hooks/use-temporary-submit";

import {
  NewbornPostpartumPhysicalExam,
  NewbornPostpartumPhysicalExamSchema,
} from "../../zod-schemas/newborn-postpartum-physical-exam";

export default function NewbornPostpartumPhysicalExamChestAndAbdomen({
  url,
  submitTrigger,
  editable,
  isAmendment,
}: PhysicalExamProps) {
  const { patient } = useContext(PatientContext);
  const { encounter } = useContext(EncounterContext);
  const { sections, questionnaire } = usePhysicalExamQuestionnaire(
    NewbornPostpartumPhysicalExamsQuestionnaires.ChestAndAbdomen,
  );
  const medplum = useMedplum();
  const form = useForm<NewbornPostpartumPhysicalExam>({
    initialValues: CHEST_AND_ABDOMEN_FORM_INITIAL_VALUES,
    validate: zodResolver(NewbornPostpartumPhysicalExamSchema),
  });

  const formSetValues = form.setValues;

  const [initialValues, questionnaireResponse, isLoading] =
    usePhysicalExamInitialValues<NewbornPostpartumPhysicalExam>(
      questionnaire?.id,
      encounter,
      formSetValues,
      CHEST_AND_ABDOMEN_FORM_INITIAL_VALUES,
    );
  const { markAsSaved } = usePhysicalExamUnsavedChanges(url, form, initialValues);
  const practitioner = medplum.getProfile() as Practitioner;

  const handleSubmit = async (values: NewbornPostpartumPhysicalExam) => {
    const mainQuestionnaireUrl = getMainQuestionnaireUrlFromEncounter(encounter!);
    if (isAmendment && questionnaireResponse) {
      await amendPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    } else {
      await createPhysicalExamQuestionnaireResponse<NewbornPostpartumPhysicalExam>(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    }
    markAsSaved();
  };

  useTemporarySubmit<NewbornPostpartumPhysicalExam>(form, submitTrigger, handleSubmit);

  if (!patient?.id || isLoading) return <Loading height={rem(96)} />;

  return (
    <form
      data-testid="physical-exam-chest-and-abdomen-form"
      onSubmit={form.onSubmit((values, _) => handleSubmit(values))}
    >
      <PhysicalExamHeader
        url={url}
        canDelete={editable}
        clientId={patient!.id!}
        title="Chest and Abdomen"
        showQuickSelection={editable}
        value={NewbornPostpartumPhysicalExamTypes.ChestAndAbdomen}
        form={form as UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>}
      >
        <PhysicalExamList
          hideDetails={true}
          sections={sections}
          editable={editable}
          hideGeneralNotes={true}
          questionnaire={questionnaire}
          form={form as UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>}
        />
      </PhysicalExamHeader>
    </form>
  );
}
